import { useState } from "react";
import { DateTime } from "luxon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { UserDetailsCard } from "@/components/users/user-details/user-details-card";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import {
  User,
  LockHistoryEntry,
  useUpdateUser as useUpdateUserMutation,
} from "@/lib/api/user";
import {
  ChevronDown,
  ChevronUp,
  Lock,
  Loader2,
  Unlock,
} from "lucide-react";

interface UserAccessControlProps {
  user: User;
}

export function UserAccessControl({ user }: UserAccessControlProps) {
  const { mutate: updateUser, isPending } = useUpdateUserMutation();
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canUpdateUsers = hasPrivilegedAccess && ability.can("update", "users");

  const [showLockDialog, setShowLockDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [showDisableIntercomDialog, setShowDisableIntercomDialog] =
    useState(false);
  const [lockReason, setLockReason] = useState("");
  const [lockReasonPublic, setLockReasonPublic] = useState("");
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const lockHistory = user.metadata?.lock_history ?? [];
  const lastLock = lockHistory.at(-1);
  const currentReason = user.locked && lastLock ? lastLock.locked_reason : null;
  const currentPublicReason =
    user.locked && lastLock ? lastLock.locked_reason_public : null;
  const intercomDisabled = user.metadata?.intercom_disabled === true;

  const resetLockDialog = () => {
    setShowLockDialog(false);
    setLockReason("");
    setLockReasonPublic("");
  };

  const handleLock = () => {
    const trimmedPublicReason = lockReasonPublic.trim();
    updateUser(
      {
        userId: user.id,
        params: {
          locked: true,
          lockedReason: lockReason.trim(),
          lockedReasonPublic: trimmedPublicReason || null,
        },
      },
      {
        onSuccess: resetLockDialog,
      },
    );
  };

  const handleUnlock = () => {
    updateUser(
      { userId: user.id, params: { locked: false } },
      { onSuccess: () => setShowUnlockDialog(false) },
    );
  };

  const handleToggleIntercom = (enabled: boolean) => {
    if (!canUpdateUsers) return;
    if (!enabled) {
      setShowDisableIntercomDialog(true);
      return;
    }
    updateUser({
      userId: user.id,
      params: { metadata: { intercom_disabled: false } },
    });
  };

  const handleConfirmDisableIntercom = () => {
    updateUser(
      {
        userId: user.id,
        params: { metadata: { intercom_disabled: true } },
      },
      { onSuccess: () => setShowDisableIntercomDialog(false) },
    );
  };

  return (
    <UserDetailsCard>
      <div className="flex flex-col gap-4">
        {/* Account Lock */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text font-medium text-neutral-500">
              Account:{" "}
            </span>
            <span
              className={user.locked ? "text-red-600" : "text-green-600"}
            >
              {user.locked ? "Locked" : "Unlocked"}
            </span>
            {user.locked && currentReason && (
              <p className="mt-1 max-w-[250px] truncate text-xs text-neutral-500">
                Reason: {currentReason}
              </p>
            )}
            {user.locked && currentPublicReason && (
              <p className="mt-0.5 max-w-[250px] truncate text-xs text-neutral-500">
                Shown to teen: {currentPublicReason}
              </p>
            )}
          </div>
          <Button
            variant={user.locked ? "default" : "destructive"}
            size="sm"
            onClick={
              user.locked
                ? () => setShowUnlockDialog(true)
                : () => setShowLockDialog(true)
            }
            disabled={!canUpdateUsers || isPending}
            type="button"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {user.locked ? (
              <>
                <Unlock className="mr-1.5 h-3.5 w-3.5" />
                Unlock User
              </>
            ) : (
              <>
                <Lock className="mr-1.5 h-3.5 w-3.5" />
                Lock User
              </>
            )}
          </Button>
        </div>

        {/* Intercom Support */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text font-medium text-neutral-500">
              Intercom Support:{" "}
            </span>
            <span
              className={intercomDisabled ? "text-red-600" : "text-green-600"}
            >
              {intercomDisabled ? "Disabled" : "Enabled"}
            </span>
          </div>
          <Switch
            checked={!intercomDisabled}
            onCheckedChange={handleToggleIntercom}
            disabled={!canUpdateUsers || isPending}
          />
        </div>

        {/* Separator */}
        <div className="border-t border-neutral-200" />

        {/* Lock History */}
        <div>
          <button
            type="button"
            className="flex w-full items-center justify-between text-sm font-medium text-neutral-500"
            onClick={() => setHistoryExpanded(!historyExpanded)}
          >
            <span>Lock History ({lockHistory.length})</span>
            {historyExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {historyExpanded && (
            <div className="mt-3">
              {lockHistory.length === 0 ? (
                <p className="text-sm text-neutral-400">No lock history</p>
              ) : (
                <div className="space-y-0">
                  {[...lockHistory].reverse().map((entry, idx) => (
                    <LockHistoryItem
                      key={`${entry.locked_at}-${idx}`}
                      entry={entry}
                      isLast={idx === lockHistory.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lock Dialog (with reason input) */}
      <Dialog
        open={showLockDialog}
        onOpenChange={(open) => {
          if (!open) resetLockDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock User Account</DialogTitle>
            <DialogDescription>
              This will prevent the teen from accessing the app. They will see a
              locked screen with support resources.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="lock-reason">
                Internal reason <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-neutral-500">
                For internal records only. Not shown to the teen.
              </p>
              <Textarea
                id="lock-reason"
                placeholder="Enter a reason for locking this account..."
                value={lockReason}
                onChange={(e) => setLockReason(e.target.value)}
                className="h-24 resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lock-reason-public">Public message (optional)</Label>
              <p className="text-xs text-neutral-500">
                Shown to the teen on the locked screen. Leave blank to show the
                default message.
              </p>
              <Textarea
                id="lock-reason-public"
                placeholder="e.g. Please contact support to restore access."
                value={lockReasonPublic}
                onChange={(e) => setLockReasonPublic(e.target.value)}
                className="h-20 resize-none"
                maxLength={280}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetLockDialog}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLock}
              disabled={isPending || !lockReason.trim()}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Lock Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock Confirmation */}
      <ConfirmActionDialog
        isOpen={showUnlockDialog}
        onClose={() => setShowUnlockDialog(false)}
        onConfirm={handleUnlock}
        title="Unlock Account"
        description="This will restore the teen's access to the app. Are you sure you want to unlock this account?"
        loading={isPending}
        confirmVariant="default"
        confirmText="Unlock"
      />

      {/* Disable Intercom Confirmation */}
      <ConfirmActionDialog
        isOpen={showDisableIntercomDialog}
        onClose={() => setShowDisableIntercomDialog(false)}
        onConfirm={handleConfirmDisableIntercom}
        title="Disable Intercom Support"
        description="This will remove the teen's ability to contact support from the locked screen. Are you sure?"
        loading={isPending}
        confirmText="Disable"
      />
    </UserDetailsCard>
  );
}

function LockHistoryItem({
  entry,
  isLast,
}: {
  entry: LockHistoryEntry;
  isLast: boolean;
}) {
  const lockedAt = DateTime.fromISO(entry.locked_at).toFormat(
    "MMM d, yyyy 'at' h:mm a",
  );
  const unlockedAt = entry.unlocked_at
    ? DateTime.fromISO(entry.unlocked_at).toFormat("MMM d, yyyy 'at' h:mm a")
    : null;

  return (
    <div className="relative flex gap-3 pb-4">
      <div className="flex flex-col items-center">
        <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-red-400" />
        {!isLast && <div className="w-px flex-1 bg-neutral-200" />}
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <p className="text-sm font-medium text-neutral-800">
          {entry.locked_reason}
        </p>
        {entry.locked_reason_public && (
          <p className="mt-0.5 text-xs italic text-neutral-500">
            Shown to teen: {entry.locked_reason_public}
          </p>
        )}
        <p className="mt-0.5 text-xs text-neutral-500">
          Locked: {lockedAt}
        </p>
        {unlockedAt ? (
          <p className="text-xs text-neutral-500">Unlocked: {unlockedAt}</p>
        ) : (
          <p className="text-xs font-medium text-red-500">Currently locked</p>
        )}
      </div>
    </div>
  );
}
